import React from 'react';
import classNames from 'classnames';

import Data, { _F } from 'data';
import { ItemIcon, WithItemTooltip, WithSkillTooltip, WithTripodTooltip } from 'tooltip';

import './SkillView.scss';
import { getIconPath } from 'common';
import { levelPoints, skillOrder } from './editor/SkillEditor';
import Slider from 'ui/slider';
import { useDrag, useDrop } from 'react-dnd';
import { chainedRef } from 'utils';

function SkillTripod({ skill, row, pvp, showPosition }) {
  const index = skill.level >= 4 + row * 3 && ([[1, 2, 3], [4, 5, 6], [7, 8]][row]).find(idx => skill.tripods?.[idx]);
  const data = index && Data.skills[skill.id].tripods?.[index - 1];
  if (!data) return <div className={classNames(`lap-skill-tripod lap-tripod-${row + 1} lap-empty`, {"lap-disabled": skill.level < 4 + row * 3})}/>;
  return (
    <WithTripodTooltip skill={skill.id} tripod={index} level={skill.tripods[index]} pvp={pvp}>
      <div className={`lap-skill-tripod lap-tripod-${row + 1}`}>
        {showPosition ? (
          <span className="lap-position">{index - row * 3}</span>
        ) : (
          <span className="lap-icon" style={{backgroundImage: `url(${getIconPath(data.icon)})`}}/>
        )}
        {skill.tripods[index] > 1 && !showPosition && <span className="lap-level">{skill.tripods[index]}</span>}
      </div>
    </WithTripodTooltip>
  );
}

function SkillRune({ skill }) {
  const data = Data.items[skill.rune];
  const rune = Data.runes[skill.rune];
  if (!data || !rune) return <div className="lap-skill-rune lap-empty"/>;
  return (
    <WithItemTooltip item={skill.rune}>
      <div className={`lap-skill-rune lap-item-icon lap-item-background lap-item-${Data.itemGrades[data.grade || 0].color}`}>
        <ItemIcon item={data}/>
      </div>
    </WithItemTooltip>
  );
}

export const SkillSlot = React.forwardRef(({ skill, delta, pvp, builder, showPosition, ...props }, ref) => {
  const data = skill && Data.skills[skill.id];
  if (!data) return <div className="lap-SkillSlot lap-empty"/>;
  return (
    <WithSkillTooltip skill={skill} pvp={pvp}>
      <div className={classNames("lap-SkillSlot",
          {"lap-awakening": data.awakening, "lap-builder": builder}, delta && `lap-delta lap-delta-${delta}`)} {...props} ref={ref}>
        <div className="lap-skill-icon" style={{backgroundImage: `url(${getIconPath(data.icon)}`}}/>
        <div className="lap-skill-name">{data.name.replace(/^.*: /, '')}</div>
        {!data.awakening && <div className="lap-separator"/>}
        {data.maxLevel > 1 && <div className="lap-skill-level">{skill.level}</div>}
        {data.tripods && (
          <div className="lap-skill-tripods">
            <SkillTripod skill={skill} row={0} pvp={pvp} showPosition={showPosition}/>
            <SkillTripod skill={skill} row={1} pvp={pvp} showPosition={showPosition}/>
            <SkillTripod skill={skill} row={2} pvp={pvp} showPosition={showPosition}/>
          </div>
        )}
        {!data.awakening && <SkillRune skill={skill}/>}
        {!!data.awakening && <div className="lap-skill-awakening">{_F('tip.name.ability_awakening')}</div>}
      </div>
    </WithSkillTooltip>
  );
});

export default function SkillView({ embed, step: parentStep, setStep: parentSetStep }) {
  const [tab1, setTab] = React.useState();
  const [localStep, localSetStep] = React.useState(() => embed.steps ? embed.steps.length - 1 : 0);

  let step = parentSetStep ? parentStep : localStep;
  if (embed.steps && !embed.steps[step]) step = embed.steps.length - 1;
  const setStep = React.useCallback(value => parentSetStep ? parentSetStep(value) : localSetStep(value), [parentSetStep]);

  const onPrevPoint = React.useCallback(() => setStep(value => Math.max(value - 1, 0)), [setStep]);
  const onNextPoint = React.useCallback(() => setStep(value => Math.min(value + 1, embed.steps.length - 1)), [setStep, embed.steps]);

  const hasAwakening = React.useMemo(() => {
    if (embed.steps) {
      for (const step of embed.steps) {
        for (const id in step) {
          if (step[id].level && Data.skills[id]?.awakening) {
            return true;
          }
        }
      }
    } else {
      for (const id in embed.data) {
        if (embed.data[id].level && Data.skills[id]?.awakening) {
          return true;
        }
      }
    }
    return false;
  }, [embed]);

  const data = embed.steps?.[step] || embed.data;
  const points = React.useMemo(() => levelPoints(data, embed.level), [data, embed.level]);

  const ids = React.useMemo(() => skillOrder(embed.class), [embed.class]);

  const tab = React.useMemo(() => {
    const tabs = Data.skillTabs[embed.class];
    if (!tabs) return -1;
    return tabs[tab1] ? tab1 : 0;
  }, [tab1, embed.class]);

  const prevLevels = React.useRef();
  const [deltaLevels, setDeltaLevels] = React.useState(null);
  React.useLayoutEffect(() => {
    const prev = prevLevels.current;
    prevLevels.current = {data, embed, tab};
    if (prev && prev.embed === embed && prev.tab === tab) {
      const diff = {};
      for (const [id, {level}] of Object.entries(data)) {
        if (!level) continue;
        if (!prev.data[id]?.level) {
          diff[id] = 'change';
        } else if (prev.data[id].level < level) {
          diff[id] = 'increase';
        } else if (prev.data[id].level > level) {
          diff[id] = 'reduce';
        }
      }
      const keys = Object.keys(diff);
      if (keys.length) {
        if (Data.skillTabs[embed.class]) {
          const tabInfo = Data.skillTabs[embed.class][tab];
          if (!keys.some(id => tabInfo?.types.includes(Data.skills[id]?.category))) {
            const toTab = Data.skillTabs[embed.class].findIndex(tab => tab.types.includes(Data.skills[keys[0]]?.category));
            if (toTab >= 0) setTab(toTab);
          }
        }
        setDeltaLevels(diff);
        const timeout = window.setTimeout(() => setDeltaLevels(null), 1);
        return () => window.clearTimeout(timeout);
      } else {
        setDeltaLevels(null);
      }
    }
  }, [data, embed, tab]);

  const tabInfo = Data.skillTabs[embed.class]?.[tab];
  const tabLimit = tabInfo?.limit == null ? 8 : tabInfo.limit;
  const awakening = ids.filter(id => Data.skills[id].awakening && data[id]?.level)[0];
  return (
    <div className="lap-SkillView">
      <div className="lap-container">
        <div className="lap-header">
          <span>
            <span className={`lap-class-icon lap-class-${embed.class}`}/>
            {_F(Data.playerClasses[embed.class].name)}
          </span>
          <span>{embed.level ? `Level ${points}` : `Points: ${points}`}</span>
        </div>
        <div className="lap-body">
          <div className="lap-frame-top"/>
          <div className="lap-frame-bottom"/>
          <div className="lap-frame-left"/>
          <div className="lap-frame-right"/>
          {!!Data.skillTabs[embed.class] && (
            <div className="lap-tabs">
              {Data.skillTabs[embed.class].map((info, idx) => (
                <div key={idx} className={classNames({"lap-active": idx === tab})} onClick={() => setTab(idx)}>
                  {info.name}
                </div>
              ))}
            </div>
          )}
          <div className="lap-skills">
            {ids.filter(id => ((tabInfo?.types ? tabInfo.types.includes(Data.skills[id].category) : true) && !Data.skills[id].awakening) &&
                (tabLimit === 0 || data[id]?.level)).map(id => (
              <SkillSlot key={id} skill={{id, ...data[id], level: tabLimit === 0 ? 1 : data[id]?.level}} delta={deltaLevels?.[id]} pvp={embed.pvp}/>
            ))}
          </div>
          {!!hasAwakening && (
            <div>
              {awakening ? (
                <SkillSlot skill={{id: awakening, level: 1}} delta={deltaLevels?.[awakening]} pvp={embed.pvp}/>
              ) : (
                <div className="lap-SkillSlot lap-awakening">
                  <div className="lap-empty-awakening"/>
                  <div className="lap-skill-awakening">Awakening</div>
                </div>
              )}
            </div>
          )}
          {!!(embed.steps && embed.steps.length > 1) && (
            <div className="lap-progression">
              <span className={classNames("lap-button lap-minus", {"lap-disabled": step <= 0})} onClick={onPrevPoint}/>
              <Slider min={0} max={embed.steps.length - 1} step={1} value={step} onChange={setStep}/>
              <span className={classNames("lap-button lap-plus", {"lap-disabled": step >= embed.steps.length - 1})} onClick={onNextPoint}/>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function useDragDrop(id, onDrag) {
  if (!onDrag) return {};

  //eslint-disable-next-line
  const [{ isDragging }, drag] = useDrag({
    type: 'SKILL',
    collect: monitor => ({ isDragging: monitor.isDragging() }),
    canDrag: () => !!id && onDrag,
    item: () => ({ type: 'SKILL', id }),
    end: (item, monitor) => {
      const result = monitor.getDropResult();
      if (onDrag && id && result?.id) onDrag(id, result.id);
    },
  }, [id, onDrag]);
  //eslint-disable-next-line
  const [{ isDropping }, drop] = useDrop({
    accept: 'SKILL',
    collect: monitor => ({ isDropping: monitor.canDrop() && monitor.isOver() }),
    canDrop: item => onDrag && item.type === 'SKILL' && id,
    drop: () => ({ id }),
  }, [id, onDrag]);
  return {isDragging, isDropping, drag, drop};
}

function CompactSkillButton({ skill, pvp, onDrag }) {
  const data = Data.skills[skill?.id];

  const {isDragging, isDropping, drag, drop} = useDragDrop(skill?.id, onDrag);

  const nodeRef = React.useMemo(() => chainedRef(drag, drop), [drag, drop]);

  if (!data) return null;
  return (
    <WithSkillTooltip skill={skill} pvp={pvp} disabled={isDragging}>
      <div className={classNames("lap-skill-icon", {"lap-dragging": isDragging, "lap-dropping": isDropping})} style={{backgroundImage: `url(${getIconPath(data.icon)}`}} ref={nodeRef}/>
    </WithSkillTooltip>
  );
}

export function CompactSkillView({ embed, onDrag }) {
  const [tab1, setTab] = React.useState(0);
  const [tabOrder, setTabOrder] = React.useState(1);
  const [animating, setAnimating] = React.useState(false);

  const data = embed.steps ? embed.steps[embed.steps.length - 1] : embed.data;

  const ids = React.useMemo(() => skillOrder(embed.class, embed.order), [embed.class, embed.order]);
  const tab = React.useMemo(() => {
    const tabs = Data.skillTabs[embed.class];
    if (!tabs) return -1;
    return tabs[tab1] ? tab1 : 0;
  }, [tab1, embed.class]);

  const tabInfo = Data.skillTabs[embed.class]?.[tab];
  const tabLimit = tabInfo?.limit == null ? 8 : tabInfo.limit;

  const awakening = ids.filter(id => Data.skills[id].awakening && data[id]?.level)[0];
  const panel = [];
  if (tabInfo && tabInfo.skip) {
    while (panel.length < tabInfo.skip) {
      panel.push(null);
    }
  }
  panel.push(...ids.filter(id => {
    if (tabInfo && tabInfo.types && !tabInfo.types.includes(Data.skills[id].category)) return false;
    if (Data.skills[id].awakening) return false;
    return tabLimit === 0 || data[id]?.level;
  }));
  while (panel.length < 8) {
    panel.push(null);
  }

  const onSetTab = React.useCallback(index => {
    const tabs = Data.skillTabs[embed.class]?.length;
    if (!tabs || tabs.length < 3) return;
    if (tab === index) return;
    if (tabs === 2) {
      setTabOrder(index === 1 ? 0 : 1);
    } else if (tabs === 3) {
      setTabOrder(index === (tab + 1) % 3 ? 1 : 0);
    }
    setTab(index);
    setAnimating(true);
  }, [embed.class, tab]);

  React.useEffect(() => {
    if (animating) {
      const timeout = window.setTimeout(() => setAnimating(false), 1);
      return () => window.clearTimeout(timeout);
    }
  }, [animating]);

  const numTabs = Data.skillTabs[embed.class]?.length || 0;
  const tabIndices = numTabs > 1 && [...Array(numTabs)].map((_, index) => (tab + index + 1 - tabOrder) % numTabs);

  return (
    <div className="lap-CompactSkillView">
      <div className={classNames("lap-skill lap-awakening", {"lap-empty": !awakening})}>
        {!!awakening && <CompactSkillButton skill={{id: awakening, level: data[awakening]?.level}} pvp={embed.pvp}/>}
      </div>
      <div className="lap-panel">
        {panel.map((id, index) => (
          <div key={index} className={classNames("lap-skill", {"lap-empty": !id})}>
            {!!id && <CompactSkillButton skill={{id, ...data[id]}} onDrag={onDrag} pvp={embed.pvp}/>}
          </div>
        ))}
      </div>
      {!!tabIndices && (
        <div className={classNames(`lap-switcher lap-switcher-${numTabs} lap-order-${tabOrder}`, {"lap-animating": animating})}>
          {tabIndices.map(index => {
            const info = Data.skillTabs[embed.class][index];
            let limit = info.limit;
            if (limit === 0) {
              limit = ids.filter(id => info.types.includes(Data.skills[id].category)).length;
            }
            return (
              <div key={index} className={`lap-tab lap-tab-${info.color}`} onClick={() => onSetTab(index)}>
                {[...Array(limit)].map((_, index) => <span key={index}/>)}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
